import { store } from '../App'
import { receiveTenants } from '../data/actions'
import { checkRedirect } from '../services/util/Request'
import { generateErrorMessage } from '../util'
import moment from 'moment'

export const csrfRef = { current: null }

const withAuth = (options) => ({
  ...(options || {}),
  headers: {
    ...(options?.headers || {}),
  },
  credentials: 'include',
  redirect: 'manual',
})

export const BASE_URL = process.env.LOCAL_BACKEND
  ? `${process.env.LOCAL_BACKEND}/tdms/v1`
  : `https://${process.env.TENANTS_API_HOST}/tdms/v1`

export const platformServicesURL = `${BASE_URL}/platformServices`
export const platformServicePackagesURL = `${BASE_URL}/platformServicePackages`
export const platformServicePermissionsURL = `${BASE_URL}/platformServicePermissions`
export const platformServicePropertiesURL = `${BASE_URL}/platformServiceProperties`
export const rolesUrl = `${BASE_URL}/permissions/roles`
export const bookingsUrl = `${BASE_URL}/platformBookings`
export const bookingPermissionsUrl = `${BASE_URL}/platformBookingPermissions`
export const reportsUrl = `${BASE_URL}/reports`
export const providersUrl = `${BASE_URL}/providers`
export const hermesUrl = `${BASE_URL}/hermes`

export const _fetch = async (url, options) => {
  checkRedirect()
  if (options && csrfRef.current) {
    const headers = options.headers ? options.headers : {}
    headers[csrfRef.current.headerName] = csrfRef.current.token
    options = { ...options, ['headers']: { ...headers } }
    if (!options.headers) {
      options = { ...options, headers }
    }
  }

  const res = await fetch(url, withAuth(options))
  if (res.status === 403) {
    const redirectUrl = encodeURIComponent(window.location.origin)
    let tdmsData = JSON.parse(sessionStorage.getItem('tdms'))
    const authorizationFailedRedirectUrl = `${window.location.origin}/#/403`

    if (tdmsData?.wasRedirected) {
      const urlParams = new URLSearchParams(window.location.search)
      const isAuthorized = urlParams.get('authorized') === 'true'
      if (!isAuthorized) {
        window.location.replace(authorizationFailedRedirectUrl)
      }
      sessionStorage.removeItem('tdms')
      throw new Error('User was already redirected!')
    }
    sessionStorage.setItem('tdms', JSON.stringify({ wasRedirected: true }))

    window.location.replace(
      `${BASE_URL}/oauth2/authorization/gas?redirect_url=${redirectUrl}&authorization_failed_redirect_url=${encodeURIComponent(
        authorizationFailedRedirectUrl,
      )}`,
    )
  }
  if (!res.ok) {
    let errorMessage = ''
    try {
      const response = await res.json()
      errorMessage = generateErrorMessage(response)
    } catch (err) {
      errorMessage = res.status
    }
    throw new Error(errorMessage)
  }

  sessionStorage.removeItem('tdms')
  return res
}

export const fetchTenantLogs = async (options) => {
  const sortMapper = {
    targetTenant: 'tenant.acronym',
    operationTypes: 'operationType',
    userId: 'user.daimlerId',
    createDate: 'createDate',
  }
  const body = {
    operationTypes: options.operationTypes,
    tenantAcronyms: options.tenants,
    diff: options.query,
  }
  const sorter = options?.sorting[0]
  const queryParams = {
    page: options.pageIndex,
    size: options.pageSize,
  }
  if (sorter && sortMapper[sorter.id]) {
    const { id, desc } = sorter
    queryParams.sort = `${sortMapper[id]}${desc ? ',DESC' : ''}`
  }
  const searchParams = new URLSearchParams(queryParams)
  const url = `${BASE_URL}/logs?${searchParams}`
  const response = await _fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  const { content, ...rest } = await response.json()
  return {
    ...rest,
    content,
  }
}

export const fetchOperationTypes = async () => {
  const response = await _fetch(`${BASE_URL}/logs/operationTypes`)
  const opTypes = await response.json()
  return {
    content: opTypes,
  }
}

export const updateUserProfileFromAlice = async (daimlerId) => {
  const url = `${usersURL}/${daimlerId}/updateFromAlice`
  const response = await _fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
  })
  return response.json()
}

export const fetchTenantRoles = async () => {
  const response = await _fetch(`${BASE_URL}/roles`)
  return response.json()
}

export const fetchTenant = async (tenantAcronym) => {
  const response = await _fetch(`${tenantsURL}/${tenantAcronym}`)
  return response.json()
}

export const fetchExpiringTenantMembers = async (tenantAcronym) => {
  const response = await _fetch(
    `${tenantsURL}/${tenantAcronym}/members/expiring`,
  )
  return response.json()
}

export const fetchArts = async () => {
  const response = await _fetch(`${BASE_URL}/dictionary/AGILE_RELEASE_TRAIN`)
  return response.json()
}

export const fetchPlatformServices = async () => {
  const response = await _fetch(platformServicesURL)
  return response.json()
}

export const fetchEmailList = async (body) => {
  const response = await _fetch(`${reportsUrl}/getEmailReport`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return response.json()
}

export const fetchCrossChargingOverview = async () => {
  const response = await _fetch(`${reportsUrl}/crossChargingOverview`)
  return response.json()
}

export const fetchCsrfToken = async () => {
  const response = await _fetch(`${BASE_URL}/csrf`)
  return response.json()
}

export const fetchLoggedInUserData = async () => {
  const promises = await Promise.all([
    _fetch(`${rolesUrl}/me`),
    fetchCsrfToken(),
  ])
  const response = {
    ssoData: await promises[0].json(),
    csrfData: await promises[1],
  }
  csrfRef.current = response.csrfData
  return response.ssoData
}

export const fetchTenantsCrossChargingOverview = async (accronym) => {
  const response = await _fetch(
    `${reportsUrl}/getTenantCostOverview/${accronym}`,
  )
  return response.json()
}

export const fetchPlatformServicePackages = async (serviceId) => {
  const response = await _fetch(`${platformServicePackagesURL}/${serviceId}`)
  return response.json()
}

export const archivePlatformServicePackages = async (servicePackageId) => {
  const response = await _fetch(
    `${platformServicePackagesURL}/${servicePackageId}/archive`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    },
  )
  return response.status === 204 ? response.text() : response.json()
}

export const fetchPackagePriceVariants = async (servicePackageSlug) => {
  const response = await _fetch(
    `${platformServicePackagesURL}/${servicePackageSlug}/priceVariants`,
  )
  return response.json()
}

export const createPackagePriceVariants = async (servicePackageSlug, body) => {
  const response = await _fetch(
    `${platformServicePackagesURL}/${servicePackageSlug}/priceVariants`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  )
  return response.json()
}

export const updatePackagePriceVariants = async (
  servicePackageSlug,
  priceVariantSlug,
  body,
) => {
  const response = await _fetch(
    `${platformServicePackagesURL}/${servicePackageSlug}/priceVariants/${priceVariantSlug}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  )
  return response.json()
}

export const deletePackagePriceVariants = async (
  servicePackageSlug,
  priceVariantSlug,
) => {
  const response = await _fetch(
    `${platformServicePackagesURL}/${servicePackageSlug}/priceVariants/${priceVariantSlug}`,
    {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    },
  )
  if (response.status === 204) {
    return response.text()
  } else {
    return response.json()
  }
}

export const fetchPlatformPackages = async () => {
  const response = await _fetch(`${platformServicePackagesURL}`)
  return response.json()
}

export const fetchBookingServicePermissions = async ({
  bookingId,
  serviceSlug,
}) => {
  const response = await _fetch(
    `${bookingPermissionsUrl}/${bookingId}/${serviceSlug}`,
  )
  return response.json()
}

export const fetchTenantMemberPermissions = async (tenantAcr) => {
  const response = await _fetch(
    `${bookingPermissionsUrl}/tenantAcr/${tenantAcr}/members`,
  )
  return response.json()
}

export const fetchUsersNebulaTeamMembership = async (userDaimlerIds) => {
  const body = userDaimlerIds
  const response = await _fetch(`${tenantsURL}/isNebulaMember`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return response.json()
}

export const putMembersToBookingServicePermission = async ({
  bookingPermissionIds,
  servicePermissionSlug,
  tenantAcronym,
}) => {
  const body = {
    bookingPermissionIds,
    servicePermissionSlug,
  }
  const response = await _fetch(`${bookingPermissionsUrl}/${tenantAcronym}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (response.status === 200) {
    return response.text()
  } else {
    return response.json()
  }
}

export const postMembersToBookingServicePermission = async ({
  members,
  bookingId,
  servicePermissionId,
  tenantAcronym,
}) => {
  const body = {
    bookingId,
    servicePermissionId,
  }
  if (Array.isArray(members)) {
    body.userDaimlerIds = members
  } else {
    body.userDaimlerId = members
  }
  const response = await _fetch(
    `${bookingPermissionsUrl}/${tenantAcronym}${
      Array.isArray(members) ? '/batch' : ''
    }`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  )
  if (response.status === 200) {
    return response.text()
  } else {
    return response.json()
  }
}

export const deleteBookingServicePermission = async (
  permissionId,
  tenantAcronym,
) => {
  const response = await _fetch(
    `${bookingPermissionsUrl}/${tenantAcronym}/${permissionId}`,
    {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    },
  )
  if (response.status === 200) {
    return response.text()
  } else {
    return response.json()
  }
}

export const fetchBookingPermissions = async ({ bookingId }) => {
  const response = await _fetch(
    `${bookingPermissionsUrl}${(bookingId && '/' + bookingId) || ''}`,
  )
  return response.json()
}

export const postPlatformPackageService = async (packageId, serviceId) => {
  const response = await _fetch(
    `${platformServicePackagesURL}/${packageId}/service/${serviceId}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    },
  )
  return response.text()
}

export const deletePlatformPackageService = async (packageId, serviceId) => {
  const response = await _fetch(
    `${platformServicePackagesURL}/${packageId}/service/${serviceId}`,
    {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    },
  )
  return response.text()
}

export const fetchPlatformPackageServices = async (packageId) => {
  const url = isNaN(packageId) ? `/slug/${packageId}/` : `/${packageId}/`
  const response = await _fetch(`${platformServicePackagesURL}${url}services`)
  return response.json()
}

export const fetchServicePermissions = async (serviceId) => {
  const response = await _fetch(`${platformServicePermissionsURL}/${serviceId}`)
  return response.json()
}

export const fetchPackagePermissions = async (packageSlug) => {
  const response = await _fetch(
    `${platformServicePermissionsURL}/packageSlug/${packageSlug}`,
  )
  return response.json()
}

export const fetchServiceProperties = async (serviceId) => {
  const response = await _fetch(
    `${platformServicePropertiesURL}/service/${serviceId}`,
  )
  return response.json()
}

export const fetchBookingServiceProperties = async (bookingId, serviceSlug) => {
  const response = await _fetch(
    `${platformServicePropertiesURL}/platformBooking/${bookingId}/${serviceSlug}`,
  )
  return response.json()
}

export const fetchBookingServiceProperty = async (
  platformBookingId,
  serviceSlug,
  propertySlug,
) => {
  const response = await _fetch(
    `${platformServicePropertiesURL}/platformBooking/${platformBookingId}/${serviceSlug}/${propertySlug}`,
  )
  return response.json()
}
export const fetchServiceProperty = async (propertyId) => {
  const response = await _fetch(`${platformServicePropertiesURL}/${propertyId}`)
  return response.json()
}

export const postServiceProperties = async (serviceId, body) => {
  const response = await _fetch(
    `${platformServicePropertiesURL}/${serviceId}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
    },
  )
  return response.json()
}

export const putBookingServiceProperty = async (
  bookingId,
  propertyId,
  body,
) => {
  const response = await _fetch(
    `${platformServicePropertiesURL}/platformBooking/${bookingId}/${propertyId}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
    },
  )
  return response.json()
}

export const putServiceProperties = async (propertyId, body) => {
  const response = await _fetch(
    `${platformServicePropertiesURL}/${propertyId}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body,
    },
  )
  return response.json()
}

export const removeServiceProperty = async (servicePropertyId) => {
  const response = await _fetch(
    `${platformServicePropertiesURL}/${servicePropertyId}`,
    {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    },
  )
  return response.text()
}

export const createServicePermission = async (serviceId, body) => {
  const response = await _fetch(
    `${platformServicePermissionsURL}/${serviceId}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
    },
  )
  return response.json()
}

export const updateServicePermission = async (servicePermissionId, body) => {
  const response = await _fetch(
    `${platformServicePermissionsURL}/${servicePermissionId}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body,
    },
  )
  return response.json()
}

export const removeServicePermission = async (servicePermissionId) => {
  const response = await _fetch(
    `${platformServicePermissionsURL}/${servicePermissionId}`,
    {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    },
  )
  return response.text()
}

export const postPlatformPackage = async (body) => {
  const response = await _fetch(`${platformServicePackagesURL}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
  })
  return response.json()
}

export const putPlatformPackage = async (body, packageId) => {
  const response = await _fetch(`${platformServicePackagesURL}/${packageId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body,
  })
  return response.json()
}

export const fetchPrices = async (servicePackageSlug, priceVariantSlug) => {
  const response = await _fetch(
    `${platformServicePackagesURL}/${servicePackageSlug}/${priceVariantSlug}/price`,
  )
  return response.json()
}

export const postPlatFormService = async ({ body }) => {
  const response = await _fetch(platformServicesURL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
  })
  return response.json()
}

export const archivePlatformServices = async (platformServiceId) => {
  const response = await _fetch(
    `${platformServicesURL}/${platformServiceId}/archive`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    },
  )
  return response.status === 204 ? response.text() : response.json()
}

export const putPlatFormService = async ({ id, body }) => {
  const response = await _fetch(`${platformServicesURL}/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body,
  })
  return response.json()
}

export const postServicePackage = async ({ body, serviceId }) => {
  const response = await _fetch(`${platformServicePackagesURL}/${serviceId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
  })
  return response.json()
}

export const putServicePackage = async ({ body, id }) => {
  const response = await _fetch(`${platformServicePackagesURL}/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body,
  })
  return response.json()
}

export const postServicePackagePrice = async ({
  body,
  servicePackageSlug,
  priceVariantSlug,
}) => {
  const response = await _fetch(
    `${platformServicePackagesURL}/${servicePackageSlug}/${priceVariantSlug}/price`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
    },
  )
  return response.json()
}

export const batchUpdateServicePackagePrices = async (
  servicePackageSlug,
  priceVariantSlug,
  body,
) => {
  const response = await _fetch(
    `${platformServicePackagesURL}/${servicePackageSlug}/${priceVariantSlug}/price/batchOverride`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  )
  return response.json()
}

export const putServicePackagePrice = async ({
  body,
  servicePackageSlug,
  priceVariantSlug,
  priceId,
}) => {
  const response = await _fetch(
    `${platformServicePackagesURL}/${servicePackageSlug}/${priceVariantSlug}/price/${priceId}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body,
    },
  )
  return response.json()
}

export const fetchTenantComplianceData = async (tenantAcr) => {
  const response = await _fetch(`${tenantsURL}/${tenantAcr}/compliance`)
  return response.json()
}

export const saveTenantComplianceData = async (tenantAcr, payload) => {
  const response = await _fetch(`${tenantsURL}/${tenantAcr}/compliance`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })

  return response.json()
}

export const fetchUserRoles = async (payload) => {
  const id = payload?.id
  const response = await _fetch(`${rolesUrl}${id ? '/' + id : ''}`)
  return response.json()
}

export const fetchAllRoles = async () => {
  const response = await _fetch(`${rolesUrl}/all`)
  return response.json()
}

export const saveUserRole = async ({ daimlerId, roles }) => {
  const response = await _fetch(`${rolesUrl}/${daimlerId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(roles),
  })
  return response.json()
}

export const fetchBookings = async ({ acronym, details, current }) => {
  const url = `${bookingsUrl}/${acronym}${(details && '/details') || ''}${
    current ? '?current=true' : ''
  }`
  const response = await _fetch(url)
  return response.json()
}

export const fetchAllBookings = async () => {
  const response = await _fetch(bookingsUrl)
  return response.json()
}

export const fetchAkamaiCpCodes = async (acronym) => {
  const response = await _fetch(
    `${tenantsURL}/environment/akamai/getCpCodes/${acronym}`,
  )
  return response.json()
}

export const invalidateAkamaiCpCodes = async (acronym, cpCodes) => {
  const response = await _fetch(
    `${tenantsURL}/environment/akamai/invalidateCpCodes/${acronym}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ cpCodes }),
    },
  )
  return response.json()
}

export const postBooking = async ({
  tenantAcr,
  priceVariantId,
  bookingData,
}) => {
  const response = await _fetch(
    `${bookingsUrl}/${tenantAcr}/${priceVariantId}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(bookingData),
    },
  )
  return response.json()
}

export const putBooking = async ({ bookingId, data }) => {
  const response = await _fetch(`${bookingsUrl}/${bookingId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  return response.json()
}

export const deleteBooking = async (bookingId) => {
  const response = await _fetch(`${bookingsUrl}/${bookingId}`, {
    method: 'DELETE',
  })
  if (response.status === 200) {
    return response.text()
  } else {
    return response.json()
  }
}

const baseURL =
  process.env.LOCAL_BACKEND || `https://${process.env.TENANTS_API_HOST}`
const usersURL = `${baseURL}/tdms/v1/users`
const tenantsURL = `${baseURL}/tdms/v1/tenants`

export const fetchAllUsers = async () => {
  const response = await _fetch(usersURL)
  const data = await response.json()
  data.sort((user1, user2) => (user1.daimlerId > user2.daimlerId ? 1 : -1))
  return data
}

export const getUser = async (daimlerId) => {
  const url = `${usersURL}/${daimlerId}`
  const response = await _fetch(url)
  return response.json()
}

export const findUsers = async (
  query = '',
  page = 0,
  size = 20,
  local = false,
) => {
  const queryParams = new URLSearchParams({
    query,
    page,
    size,
  })
  if (local) {
    queryParams.append('lookupEngine', 'LOCAL')
  }
  const url = new URL(`${usersURL}/find?`)
  const response = await _fetch(url + queryParams)
  return response.json()
}

export const submitCurrentUser = async (daimlerId, user) => {
  const userResponse = await _fetch(`${usersURL}/me/${daimlerId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(user),
  })
  return userResponse.json()
}

export const postMembership = async ({ acronym, user }) => {
  const expiryDate = moment().add(3, 'months').format('YYYY-MM-DD')
  const body = JSON.stringify({
    daimlerId: user.daimlerId,
    roles: ['OTHER'],
    tenantUserRoleExpiryDate: expiryDate,
  })
  try {
    await _fetch(`${tenantsURL}/${acronym}/members`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
    })
    const allTenants = { ...store.getState().tenants.allTenants }
    allTenants[acronym].members = [
      ...allTenants[acronym].members,
      {
        ...user,
        roles: ['OTHER'],
        tenantMemberUntil: expiryDate,
      },
    ]
    store.dispatch(receiveTenants(allTenants))
    return user
  } catch {
    return false
  }
}

export const deleteMembership = async ({ acronym, daimlerId }) => {
  try {
    await _fetch(`${tenantsURL}/${acronym}/members/${daimlerId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    })
    const allTenants = { ...store.getState().tenants.allTenants }
    allTenants[acronym].members = allTenants[acronym].members.filter(
      (user) => user.daimlerId !== daimlerId,
    )
    store.dispatch(receiveTenants(allTenants))
    return true
  } catch {
    return false
  }
}

export const fetchTenantMember = async (tenantAcr) => {
  const response = await _fetch(`${tenantsURL}/${tenantAcr}`)
  return response.json()
}

export const deleteTenantMember = async ({ tenantAcr, daimlerId }) => {
  const response = await _fetch(
    `${tenantsURL}/${tenantAcr}/members/${daimlerId}`,
    {
      method: 'DELETE',
    },
  )
  return response.text()
}

export const batchDeleteTenantMembers = async ({ tenantAcr, daimlerIds }) => {
  const response = await _fetch(
    `${tenantsURL}/${tenantAcr}/members/batch?daimlerIds=${daimlerIds.join(
      ',',
    )}`,
    {
      method: 'DELETE',
    },
  )
  return response.text()
}

export const batchAddTenantMembers = async ({
  tenantAcr,
  daimlerIds,
  roles,
  tenantUserRoleExpiryDate,
}) => {
  const response = await _fetch(`${tenantsURL}/${tenantAcr}/members/batch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      roles,
      daimlerIds,
      tenantUserRoleExpiryDate,
    }),
  })
  return response.text()
}

export const setTenantMembersExpiryDate = async ({
  tenantAcr,
  daimlerIds,
  tenantUserRoleExpiryDate,
}) => {
  const response = await _fetch(
    `${tenantsURL}/${tenantAcr}/members/setUsersExpiryDate`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        tenantUserRoleExpiryDate,
        daimlerIds,
        confirmedReviewedMembersSelectedForExtension: true,
      }),
    },
  )
  return response.text()
}

export const updateTenantMember = async ({
  tenantAcr,
  daimlerId,
  roles,
  tenantUserRoleExpiryDate,
}) => {
  const response = await _fetch(
    `${tenantsURL}/${tenantAcr}/members/${daimlerId}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        roles,
        tenantUserRoleExpiryDate,
      }),
    },
  )
  return response.text()
}

export const generateExport = async (providers, format) => {
  const url = new URL(
    `${BASE_URL}/reports/crossChargingOverview/${format.toUpperCase()}`,
  )
  if (providers) {
    url.searchParams.append(
      'provider',
      Array.isArray(providers) ? providers.join(',') : providers,
    )
  }
  const response = await _fetch(url.toString())
  return response.blob()
}

export const putMembership = async ({ acronym, user }) => {
  try {
    await _fetch(`${tenantsURL}/${acronym}/members/${user.daimlerId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        tenantUserRoleExpiryDate: user.tenantUserRoleExpiryDate,
        roles: user.roles,
      }),
    })
    const allTenants = { ...store.getState().tenants.allTenants }
    const updatedMembers = [...allTenants[acronym].members]
    const indexOfUpdatedMember = updatedMembers.findIndex(
      (u) => u.daimlerId === user.daimlerId,
    )
    const updatedMember = updatedMembers[indexOfUpdatedMember]
    updatedMembers[indexOfUpdatedMember] = {
      ...updatedMember,
      roles: user.roles,
      tenantMemberUntil: user.tenantUserRoleExpiryDate,
    }
    allTenants[acronym].members = updatedMembers
    store.dispatch(receiveTenants(allTenants))
    return true
  } catch {
    return false
  }
}

export const changeTenantProvider = async ({ acronym, provider }) => {
  const response = await _fetch(
    `${tenantsURL}/${acronym}/provider/${provider}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
    },
  )
  return response.text()
}

export const getProviders = async () => {
  const response = await _fetch(providersUrl)
  return response.json()
}

export const getAllProviders = async () => {
  const response = await _fetch(`${providersUrl}?includeDeleted=true`)
  return response.json()
}

export const getProvider = async (slug) => {
  const response = await _fetch(`${providersUrl}/${slug}`)
  return response.json()
}

export const createProvider = async (providersObj) => {
  const response = await _fetch(providersUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(providersObj),
  })
  return response.json()
}

export const updateProvider = async (providersObj) => {
  const response = await _fetch(`${providersUrl}/${providersObj.slug}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(providersObj),
  })
  return response.json()
}

export const deleteProvider = async (slug) => {
  const response = await _fetch(`${providersUrl}/${slug}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  })
  return response.text()
}

export const getProviderUsers = async (providerSlug) => {
  const response = await _fetch(`${providersUrl}/${providerSlug}/users`)
  return response.json()
}

export const getTdmsUsers = async () => {
  const response = await _fetch(`${rolesUrl}/assigned`)
  return response.json()
}

export const addUsersToProvider = async (providerSlug, userDaimlerIds) => {
  const response = await _fetch(`${providersUrl}/${providerSlug}/users`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ daimlerIds: userDaimlerIds }),
  })
  return response.json()
}

export const removeUsersFromProvider = async (providerSlug, userDaimlerIds) => {
  const response = await _fetch(`${providersUrl}/${providerSlug}/users`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ daimlerIds: userDaimlerIds }),
  })
  return response.text()
}

export const addAdminUsersToProvider = async (providerSlug, userDaimlerIds) => {
  const response = await _fetch(`${providersUrl}/${providerSlug}/admins`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ daimlerIds: userDaimlerIds }),
  })
  return response.json()
}

export const getProviderUserRoles = async () => {
  const response = await _fetch(`${rolesUrl}/me/providers`)
  return response.json()
}

export const fetchWebHooks = async () => {
  const response = await _fetch(`${hermesUrl}/webhooks`)
  return response.json()
}

export const fetchWebHook = async (webHookId) => {
  const response = await _fetch(`${hermesUrl}/webhooks/${webHookId}`)
  return response.json()
}

export const createWebHook = async (newWebHook) => {
  const response = await _fetch(`${hermesUrl}/webhooks`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(newWebHook),
  })
  return response.json()
}

export const updateWebHook = async (newWebHook) => {
  const response = await _fetch(`${hermesUrl}/webhooks/${newWebHook.id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(newWebHook),
  })
  return response.json()
}

export const deleteWebHook = async (webHookId) => {
  const response = await _fetch(`${hermesUrl}/webhooks/${webHookId}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  })
  return response.status === 200 ? response.text() : response.json()
}

export const fetchWebHookProducers = async () => {
  const response = await _fetch(`${hermesUrl}/producers`)
  return response.json()
}

export const fetchWebHookProducer = async (producerSlug) => {
  const response = await _fetch(`${hermesUrl}/producers/${producerSlug}`)
  return response.json()
}

export const fetchWebHookEvents = async (producerSlug) => {
  const response = await _fetch(
    `${hermesUrl}/producers/${producerSlug}/events?page=0&size=1000`,
  )
  return response.json()
}

export const fetchWebHookProviders = async (producerSlug) => {
  const response = await _fetch(
    `${hermesUrl}/producers/${producerSlug}/providers?page=0&size=1000`,
  )
  return response.json()
}
